// src/pages/AgentConsole.tsx
import React, { useMemo, useState } from 'react';
import { FaPhoneAlt, FaPhoneSlash, FaMicrophone, FaMicrophoneSlash, FaUser, FaClock, FaCheckCircle } from 'react-icons/fa';

type IncomingCall = {
  callId: string;
  userId?: string;
  userName?: string;
  issueType?: string;
  createdAt?: string | Date;
};

function formatDuration(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

const AgentConsole: React.FC = () => {
  const [agentName, setAgentName] = useState<string>(() => {
    return localStorage.getItem('forfeo_agent_name') || 'Agent Forfeo';
  });

  const [isAgentEnabled, setIsAgentEnabled] = useState(false);

  const [incomingCalls, setIncomingCalls] = useState<IncomingCall[]>([]);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);

  const [callDuration, setCallDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  // État simulé car WebRTCService est temporairement retiré
  const [status, setStatus] = useState({
    isConnected: false,
    isCalling: false,
    hasError: false,
    errorMessage: '',
    callId: null
  });

  const activeCall = useMemo(() => {
    if (!activeCallId) return null;
    return incomingCalls.find((c) => c.callId === activeCallId) || null;
  }, [incomingCalls, activeCallId]);


  const enableAgent = async () => {
    try {
      localStorage.setItem('forfeo_agent_name', agentName);
      // await WebRTCService.enableAgentMode(agentName); // Désactivé temporairement
      setIsAgentEnabled(true);
      console.log("Mode agent activé (Simulation)");
    } catch (e) {
      console.error(e);
      alert("Impossible d'activer le mode agent. Vérifie le backend et VITE_BACKEND_URL.");
    }
  };

  const answer = async (callId: string) => {
    try {
      setActiveCallId(callId);
      setMuted(false);
      setCallDuration(0);
      setStatus(prev => ({ ...prev, isCalling: true, isConnected: true }));
      // await WebRTCService.answerCall(callId); // Désactivé temporairement
      console.log("Réponse à l'appel", callId);
    } catch (e) {
      console.error(e);
      alert("Impossible de répondre à l'appel. Vérifie les permissions micro.");
    }
  };

  const hangup = async () => {
    try {
      // await WebRTCService.endCall(); // Désactivé temporairement
      console.log("Appel raccroché");
    } finally {
      setActiveCallId(null);
      setMuted(false);
      setCallDuration(0);
      setStatus(prev => ({ ...prev, isCalling: false, isConnected: false }));
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    // WebRTCService.toggleMute(next); // Désactivé temporairement
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-900 text-white p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-black">Console Agent</h1>
            <p className="text-white/70 text-sm">Mode WebRTC temporairement désactivé</p>
          </div>

          <div className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-2xl p-3">
            <FaUser className="text-white/80" />
            <input
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="bg-transparent outline-none text-sm w-56 placeholder:text-white/40"
              placeholder="Nom de l'agent"
              disabled={isAgentEnabled}
            />
            {!isAgentEnabled ? (
              <button
                onClick={enableAgent}
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 px-4 py-2 rounded-xl font-bold text-sm transition"
              >
                Devenir disponible
              </button>
            ) : (
              <span className="text-xs bg-green-500/20 border border-green-500/30 px-3 py-1 rounded-xl flex items-center gap-2">
                <FaCheckCircle className="text-green-300" />
                Agent en ligne
              </span>
            )}
          </div>
        </div>

        {/* Status bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <div className="bg-white/10 border border-white/10 rounded-2xl p-4">
            <p className="text-xs text-white/60 mb-1">État</p>
            <p className="font-bold">
              {status.isConnected ? '✅ En appel' : status.isCalling ? '📞 Connexion...' : '🟦 En attente'}
            </p>
          </div>

          <div className="bg-white/10 border border-white/10 rounded-2xl p-4">
            <p className="text-xs text-white/60 mb-1">Durée</p>
            <p className="font-black text-xl flex items-center gap-2">
              <FaClock className="text-white/70" />
              {formatDuration(callDuration)}
            </p>
          </div>

          <div className="bg-white/10 border border-white/10 rounded-2xl p-4">
            <p className="text-xs text-white/60 mb-1">Micro</p>
            <button
              onClick={toggleMute}
              disabled={!status.isCalling}
              className={`w-full px-4 py-2 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 ${
                muted ? 'bg-red-500/30 hover:bg-red-500/40 border border-red-500/30' : 'bg-white/10 hover:bg-white/20 border border-white/15'
              } ${!status.isCalling ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {muted ? <FaMicrophoneSlash /> : <FaMicrophone />}
              {muted ? 'Micro coupé' : 'Micro actif'}
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Incoming list */}
          <div className="bg-white/10 border border-white/10 rounded-3xl p-5">
            <h2 className="font-black text-lg mb-4">Appels entrants</h2>

            {incomingCalls.length === 0 ? (
              <div className="text-white/60 text-sm bg-black/20 rounded-2xl p-6 text-center">
                Aucun appel entrant pour le moment.
              </div>
            ) : (
              <div className="space-y-3">
                {incomingCalls.map((call) => {
                  const isActive = call.callId === activeCallId;
                  return (
                    <div
                      key={call.callId}
                      className={`rounded-2xl p-4 border transition ${
                        isActive ? 'bg-green-500/10 border-green-500/30' : 'bg-black/20 border-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-bold text-sm">{call.userName || 'Client'}</p>
                          <p className="text-xs text-white/60 break-all">Call ID: {call.callId}</p>
                          <p className="text-xs text-white/70 mt-1">
                            Type: <span className="font-bold">{call.issueType || 'general'}</span>
                          </p>
                        </div>

                        <button
                          onClick={() => answer(call.callId)}
                          disabled={status.isCalling && !isActive}
                          className={`px-4 py-2 rounded-xl font-bold text-sm transition flex items-center gap-2 ${
                            isActive
                              ? 'bg-green-500/30 border border-green-500/30'
                              : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                          } ${status.isCalling && !isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title="Répondre"
                        >
                          <FaPhoneAlt />
                          {isActive ? 'En cours' : 'Répondre'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Active call panel */}
          <div className="bg-white/10 border border-white/10 rounded-3xl p-5">
            <h2 className="font-black text-lg mb-4">Appel en cours</h2>

            {!status.isCalling ? (
              <div className="text-white/60 text-sm bg-black/20 rounded-2xl p-6 text-center">
                Aucun appel en cours. Attends un appel entrant puis clique “Répondre”.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-black/20 rounded-2xl p-4 border border-white/10">
                  <p className="text-xs text-white/60 mb-1">Call ID</p>
                  <p className="font-mono text-sm break-all">{status.callId || activeCallId || '—'}</p>

                  <div className="mt-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-white/60">Statut</p>
                      <p className="font-bold">{status.isConnected ? '✅ Connecté' : '📞 Connexion...'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Durée</p>
                      <p className="font-black text-xl">{formatDuration(callDuration)}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={hangup}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-2xl font-black transition flex items-center justify-center gap-2"
                >
                  <FaPhoneSlash />
                  Raccrocher
                </button>

                {status.hasError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-sm text-red-200">
                    Erreur: {status.errorMessage || 'Une erreur est survenue.'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center text-xs text-white/50">
          Forfeo • Agent Console • WebRTC (Désactivé)
        </div>
      </div>
    </div>
  );
};

export default AgentConsole;
